import api from '@/api/base';
import type { ApiResponse, UserClientModel } from '@/api/types';

// API URLs for user endpoints
const URLS = {
  users: '/user',
  userMe: '/user/me',
  userById: (id: string) => `/user/${id}`,
  userStatus: (id: string) => `/user/${id}/status`,
  userProfile: (id: string) => `/user/${id}/profile`,
} as const;

// Request bodies are loosely typed; the profile CRUD endpoints return the
// shared { status, data } envelope wrapping a client user.
type UserPayload = Record<string, unknown>;

// Users API methods
const usersApi = {
  // Get current user (me)
  getMe: (token?: string) => {
    return api.get<ApiResponse<UserClientModel>>(URLS.userMe, { token });
  },

  // Get user details (alias for getMe, used by authService)
  getUserDetails: (token?: string) => {
    return api.get<ApiResponse<UserClientModel>>(URLS.userMe, { token });
  },

  // EDS Authentication: Get GUID for EDS signing (no auth required) — bare
  getEDSGuid: () => {
    return api.get<unknown>(URLS.users);
  },

  // EDS Authentication: Verify EDS signature and complete authentication.
  // Returns { status, data: { token, user } }.
  verifyEDSSignature: ({
    pkcs,
    infoCert,
  }: {
    pkcs?: unknown;
    infoCert?: unknown;
  }) => {
    return api.post<ApiResponse<{ token?: string; user?: UserClientModel }>>(
      URLS.users,
      { pkcs, infoCert },
      { withCredentials: true },
    );
  },

  // Update current user profile (me)
  updateMe: (profileData: UserPayload, token?: string) => {
    return api.patch<ApiResponse<UserClientModel>>(URLS.users, profileData, {
      token,
    });
  },

  // Get all users
  getAllUsers: (token?: string) => {
    return api.get<ApiResponse<UserClientModel[]>>(URLS.users, { token });
  },

  // Get user by ID
  getUserById: (id: string, token?: string) => {
    return api.get<ApiResponse<UserClientModel>>(URLS.userById(id), { token });
  },

  // Create new user — POSTs to the client register endpoint; result unused
  createUser: (userData: UserPayload, token?: string) => {
    return api.post<unknown>(URLS.users, userData, { token });
  },

  // Update user
  updateUser: (id: string, userData: UserPayload, token?: string) => {
    return api.patch<ApiResponse<UserClientModel>>(
      URLS.userById(id),
      userData,
      { token },
    );
  },

  // Delete user — no backing DELETE route on the server; result is opaque
  deleteUser: (id: string, token?: string) => {
    return api.delete<unknown>(URLS.userById(id), { token });
  },

  // Update user status — no backing route on the server; result is opaque
  updateUserStatus: (id: string, status: unknown, token?: string) => {
    return api.patch<unknown>(URLS.userStatus(id), { status }, { token });
  },

  // Update user profile — no backing route on the server; result is opaque
  updateUserProfile: (id: string, profileData: UserPayload, token?: string) => {
    return api.patch<unknown>(URLS.userProfile(id), profileData, { token });
  },
};

export default usersApi;
